/**
 * Tiny rule-based NLP toolkit for the portfolio chat bot.
 *
 * No models involved: text is normalised, tokenised, stemmed and then
 * matched against keyword lists with a little typo tolerance
 * (prefix + Damerau-Levenshtein distance).
 */

// Filler words that carry no meaning for intent detection. Kept out of the
// fuzzy matcher too, so e.g. "there" never fuzzes into "where".
const STOPWORDS = new Set([
  "a", "an", "the", "is", "are", "was", "were", "be", "been", "being", "am",
  "do", "does", "did", "doing", "done", "of", "to", "in", "on", "at", "for",
  "by", "as", "into", "with", "from", "and", "or", "but", "so", "if", "than",
  "i", "me", "my", "we", "our", "you", "your", "yours", "u", "ur",
  "he", "him", "his", "himself", "she", "her", "they", "them", "their",
  "it", "its", "this", "that", "these", "those", "there", "here", "same",
  "what", "whats", "which", "whom", "whos", "can", "could", "would",
  "should", "will", "shall", "may", "might", "must", "please", "pls", "plz",
  "tell", "say", "let", "know", "give", "show", "share", "want", "like",
  "some", "any", "all", "much", "many", "very", "really", "just", "also",
  "about", "regarding", "suraj", "verma", "mr", "sir", "bro", "get", "got",
  "has", "have", "had", "having", "then", "ok", "okay",
]);

// Words that point back at whatever was discussed in the previous turn.
const PRONOUNS = new Set([
  "there", "they", "them", "their", "it", "its", "that", "this", "same",
  "those", "these",
]);

export const normalize = (text = "") =>
  text
    .toLowerCase()
    .replace(/['’]s\b/g, "")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const SUFFIX_RULES = [
  ["ies", "y"],
  ["ied", "y"],
  ["ing", ""],
  ["ed", ""],
  ["es", ""],
  ["s", ""],
];

export const stem = (word) => {
  if (word.length <= 3 || word.endsWith("ss")) return word;
  for (const [suffix, replacement] of SUFFIX_RULES) {
    if (word.endsWith(suffix) && word.length - suffix.length >= 3) {
      return word.slice(0, -suffix.length) + replacement;
    }
  }
  return word;
};

// Optimal string alignment distance (Levenshtein + adjacent transpositions).
const editDistance = (a, b) => {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d = Array.from({length: rows}, (_, i) => [i, ...Array(cols - 1).fill(0)]);
  for (let j = 0; j < cols; j++) d[0][j] = j;

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[rows - 1][cols - 1];
};

/**
 * Does a user token match a keyword token? Short words must match exactly;
 * longer ones tolerate shared prefixes ("experienc" ~ "experience") and
 * small typos ("skils" ~ "skill").
 */
const tokenMatches = (token, keyword, fuzzy) => {
  if (token === keyword) return true;
  if (!fuzzy) return false;

  const shortest = Math.min(token.length, keyword.length);
  if (shortest >= 5 && (token.startsWith(keyword) || keyword.startsWith(token))) {
    return true;
  }
  if (shortest >= 5 && Math.abs(token.length - keyword.length) <= 2) {
    return editDistance(token, keyword) <= (shortest >= 8 ? 2 : 1);
  }
  return false;
};

/**
 * Break a message down into everything the matchers need.
 *  - words:   every stemmed token, in order (used for phrase matching)
 *  - content: stemmed tokens minus stopwords (used for keyword matching)
 */
export const analyze = (text) => {
  const raw = normalize(text).split(" ").filter(Boolean);
  const words = raw.map((w) => ({raw: w, stem: stem(w), fuzzy: !STOPWORDS.has(w)}));
  const content = words.filter((w) => w.fuzzy);

  return {
    raw,
    words,
    content,
    hasPronoun: raw.some((w) => PRONOUNS.has(w)),
  };
};

/**
 * Pre-process a keyword ("tech stack", ["resume", 2]) into
 * {stems: [...], weight, exact}. Multi-word phrases weigh more by default
 * since they are a much stronger signal than a single word.
 *
 * A leading "=" ("=companies") skips stemming and fuzziness so the exact
 * word form matters, e.g. plural "companies" vs singular "company".
 */
export const compileKeyword = (keyword) => {
  const [phrase, weight] = Array.isArray(keyword) ? keyword : [keyword];
  const exact = phrase.startsWith("=");
  const parts = normalize(phrase).split(" ").filter(Boolean);
  const stems = exact ? parts : parts.map(stem);
  return {stems, exact, weight: weight ?? (stems.length > 1 ? stems.length + 0.5 : 1)};
};

// Typos can stem badly ("skils" -> "skil"), so the raw word gets a fuzzy try too.
const wordMatches = (word, keyword, exact, fuzzy) => {
  if (exact) return word.raw === keyword;
  return tokenMatches(word.stem, keyword, fuzzy) || (fuzzy && tokenMatches(word.raw, keyword, true));
};

export const matchesKeyword = ({stems, exact}, analysis) => {
  if (stems.length === 1) {
    return analysis.content.some((w) => wordMatches(w, stems[0], exact, true));
  }

  const {words} = analysis;
  for (let start = 0; start + stems.length <= words.length; start++) {
    const isMatch = stems.every((s, offset) => {
      const word = words[start + offset];
      return wordMatches(word, s, exact, word.fuzzy);
    });
    if (isMatch) return true;
  }
  return false;
};

/** Sum of weights of every keyword present in the message. */
export const scoreKeywords = (compiledKeywords, analysis) =>
  compiledKeywords.reduce(
    (score, keyword) => (matchesKeyword(keyword, analysis) ? score + keyword.weight : score),
    0
  );
